import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { DeliveryLogResponse, DeliverySummary } from '../types/delivery';

export interface DeliveryLogParams {
  status?: string;
  source?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useDeliveryLog(params: DeliveryLogParams) {
  return useQuery<DeliveryLogResponse>({
    queryKey: ['delivery-log', params],
    queryFn: () => api.email.log(params),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useDeliverySummary() {
  return useQuery<DeliverySummary>({
    queryKey: ['delivery-summary'],
    queryFn: () => api.email.logSummary(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useDeliveryMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery-log'] });
    queryClient.invalidateQueries({ queryKey: ['delivery-summary'] });
  };

  const retry = useMutation({
    mutationFn: (id: number) => api.email.retry(id),
    onSuccess: invalidate,
  });

  const resend = useMutation({
    mutationFn: (args: { guestId: number; email?: string }) => api.email.resend(args.guestId, args.email),
    onSuccess: invalidate,
  });

  const retryFailed = useMutation({
    mutationFn: () => api.email.retryFailed(),
    onSuccess: invalidate,
  });

  return { retry, resend, retryFailed };
}
